import { useTranslation } from 'react-i18next';
import type { NavigateFunction } from 'react-router-dom';
import {
  parkPaymentResumeReturnTarget,
  savePlatformReturnTarget,
} from '../../../../shared/session/returnTarget.js';
import {
  readLastKnownSalesPointId,
  readLastKnownSalesPointSlug} from '../../../../shared/session/lastKnownSalesPoint.js';
import { resolveCustomerSignInPath } from '../../../auth/customerAuthPaths.js';
import { IntentLedKioskMissingPanel } from '../../../shop/components/IntentLedKioskMissingPanel.js';
import {
  isGloballySignedInChrome,
  navigateToShopEntry} from '../../../hub/customerHubPaths.js';
import { KioskShopHelpModal } from '../../../shop/components/KioskShopHelpModal.js';
import { useCustomerSession } from '../../../../shared/session/CustomerSessionProvider.js';

export interface PhoneFirstCheckoutMissingSessionViewProps {
  readonly tenantCode: string;
  readonly checkoutHelpOpen: boolean;
  readonly setCheckoutHelpOpen: (open: boolean) => void;
  readonly locationPathAndSearch: string;
  readonly navigate: NavigateFunction;
}

export function PhoneFirstCheckoutMissingSessionView(
  props: PhoneFirstCheckoutMissingSessionViewProps,
): JSX.Element {
  const { t } = useTranslation('customer');
  const {
    tenantCode,
    checkoutHelpOpen,
    setCheckoutHelpOpen,
    locationPathAndSearch,
    navigate} = props;
  const { session } = useCustomerSession();
  const signedIn = isGloballySignedInChrome({ session });

  const handleContinueToAuth = (): void => {
    // G1 — navigate to sign-in only after elevate park succeeds.
    const parked = parkPaymentResumeReturnTarget(locationPathAndSearch, {
      tenantCode,
      source: 'checkout',
    });
    if (parked === null) {
      return;
    }
    navigate(resolveCustomerSignInPath(tenantCode));
  };

  const handleSecondaryShop = (): void => {
    navigateToShopEntry({
      tenantCode,
      session,
      navigate,
      rememberedSalesPointSlug: readLastKnownSalesPointSlug(tenantCode),
      rememberedSalesPointId: readLastKnownSalesPointId(tenantCode),
      onNeedKioskHelp: () => setCheckoutHelpOpen(true)});
  };

  return (
    <>
      <IntentLedKioskMissingPanel
        invalidQuery={false}
        primaryText={t('checkout.missingSessionPrimary')}
        helperText={t('checkout.missingSessionHelper')}
        signedIn={signedIn}
        onContinueToAuth={handleContinueToAuth}
        onGoToAccount={() => {
          if (signedIn) {
            navigate('/account');
            return;
          }
          savePlatformReturnTarget('/account');
          navigate('/sign-in');
        }}
        onSecondaryShop={handleSecondaryShop}
      />
      <KioskShopHelpModal open={checkoutHelpOpen} onClose={() => setCheckoutHelpOpen(false)} />
    </>
  );
}
